import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Typography, ColumnLayout, Column, ModalBody,
} from '@visionplanner/ui-react-material';
import moment from 'moment';
import { SalesOnBoarding } from '../sales.context';
import { saveReviewDetails } from '../sales-onboarding-partner.request';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import { StyledModalFooter } from '../../styles';

const NOT_ANSWERED = 'N/A';

const dateFormatting = ({ date }) => ((date !== null) ? moment(date).format('DD-MM-YYYY') : NOT_ANSWERED);

/** Component to display sales-onboarding
 * partner overview details
 * */
const Review = ({
  officeTypes,
  originTypes,
  invoiceTypes,
  setEndOnBoarding,
}) => {
  const {
    activeStep, setActiveStep, state,
  } = useContext(SalesOnBoarding);

  const {
    generalInformation, onboardingInformation, invoicingInformation, partnerId, lastModified,
  } = state;

  const handleSubmit = useAsyncCallback(async () => {
    await saveReviewDetails(partnerId, lastModified);
    setEndOnBoarding(true);
  }, [partnerId, setEndOnBoarding]);

  const typeOfOffice = useMemo(() => officeTypes.find((item) => item.value === generalInformation.typeOfOffice), [generalInformation.typeOfOffice, officeTypes]);
  const typeOfOrigin = useMemo(() => originTypes.find((item) => item.value === generalInformation.advisorOriginType), [generalInformation.advisorOriginType, originTypes]);
  const typeOfInvoice = useMemo(() => invoiceTypes.find((item) => item.value === invoicingInformation.typeOfInvoice), [invoicingInformation.typeOfInvoice, invoiceTypes]);

  return (
    <div className="flex-column full-height" data-ta="review-submit-details">
      <ModalBody>
        <Typography use="h6" className="flex mar-b-sm">Controleren en bevestigen</Typography>
        <ColumnLayout>
          <Column span={12} className="flex-column">
            <Typography dataTa="sales-review-general-info" use="h6">Algemene informatie</Typography>
            <Typography dataTa="sales-review-general-info-firmname" use="subtitle2" className="mar-t-xs">{`Kantoornaam: ${generalInformation.firmName}`}</Typography>
            <Typography dataTa="sales-review-general-info-typeOfOffice" use="subtitle2" className="mar-t-xs">{`Type kantoor: ${typeOfOffice?.label}`}</Typography>
            <Typography dataTa="sales-review-general-info-typeOfOrigin" use="subtitle2" className="mar-t-xs">{`Herkomst: ${typeOfOrigin?.label}`}</Typography>
            <Typography dataTa="sales-review-general-info-infinieLicenceNumber" use="subtitle2" className="mar-t-xs">{`Infine Licentienummer: ${generalInformation.infineLicenceNumber || NOT_ANSWERED}`}</Typography>
          </Column>
          <Column span={12} className="flex-column">
            <Typography dataTa="sales-review-personal-info" use="h6">Contactpersoon/eerste gebruiker</Typography>
            <Typography dataTa="sales-review-personal-info-fullname" use="subtitle2" className="mar-t-xs">
              {`Naam:
              ${onboardingInformation.firstContactPersonFirstName}
              ${onboardingInformation.firstContactPersonMiddleName || ''}
              ${onboardingInformation.firstContactPersonLastName}`}
            </Typography>
            <Typography dataTa="sales-review-personal-info-firstContactEmail" use="subtitle2" className="mar-t-xs">{`E-mailadres: ${onboardingInformation.firstContactPersonEmail}`}</Typography>
          </Column>
          <Column span={12} className="flex-column">
            <Typography dataTa="sales-review-invoicing-information" use="h6">Facturatie</Typography>
            <Typography dataTa="sales-review-invoicing-information-startDate" use="subtitle2" className="mar-t-xs">
              {`Factuur vanaf: ${dateFormatting({ date: invoicingInformation.invoiceStartDate })}`}
            </Typography>
            <Typography dataTa="sales-review-invoicing-information-typeOfInvoice" use="subtitle2" className="mar-t-xs">
              {`Type factuur: ${typeOfInvoice?.label}`}
            </Typography>
            <Typography dataTa="sales-review-invoicing-information-onboardingFeePerMonth" use="subtitle2" className="mar-t-xs">
              {`Onboarding fee per maand: € ${invoicingInformation.onboardingFeePerMonth} t/m: ${dateFormatting({ date: invoicingInformation.onboardingEndDate })}`}
            </Typography>
            <Typography dataTa="sales-review-invoicing-information-isMinimumFee" use="subtitle2" className="mar-t-xs">
              {`Minimum factuurbedrag: € ${!invoicingInformation.isMinimumFee ? invoicingInformation.customAmountPerMonth : invoicingInformation.minimumFee}`}
            </Typography>
            <Typography dataTa="sales-review-invoicing-information-reductionPercentage" use="subtitle2" className="mar-t-xs">
              {`Factuurkorting: ${invoicingInformation.reductionPercentage} % t/m: ${dateFormatting({ date: invoicingInformation.reductionEndDate })}`}
            </Typography>
          </Column>
        </ColumnLayout>
      </ModalBody>
      <StyledModalFooter>
        <Button buttonType="secondary" dataTa="go-to-invoicing-information" onClick={() => setActiveStep(activeStep - 1)}>Vorige</Button>
        <Button dataTa="review-submit" onClick={handleSubmit}>Bevestigen</Button>
      </StyledModalFooter>
    </div>
  );
};

export default Review;

Review.propTypes = {
  /** function to set current or active step */
  setActiveStep: PropTypes.func,
  /** current step */
  activeStep: PropTypes.number,
  /** list of office types */
  officeTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /** list of service types */
  serviceTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};
